import { weenatIntl } from '@weenat/wintl';
import intersection from 'lodash-es/intersection';
import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import moment from 'moment-timezone';
import { fertigationMetrics, hydricStressMetrics, isAggregateEnabledMetric, isMetricWithHorizon } from '../../enums/MetricIds';
import TimeSteps, { underDayTimeSteps } from '../../enums/TimeSteps';
import { AggregationType } from '../../resources/measurements.type';
import { usePlanFeat } from '../billing';
import { formatHistoryDataForCharts } from '../charts';
import { isReactNative } from '../utils';
import { timestampToMoment } from './datesUtilities';
export const useMaximumForecastDate = (timezone, billingStatus) => {
    const feat = usePlanFeat('FEAT-FORECAST-NUMDAYS', billingStatus);
    const numberOfDays = feat ? feat.params + 1 : 1;
    return moment().tz(timezone).startOf('day').add(numberOfDays, 'days').toDate();
};
/** {@link PredefinedPeriodIds} allowed on the history page */
export const allowedHistoryPeriods = [
    'current_day',
    'current_week',
    'last_7_days',
    'current_month',
    'current_year',
    'custom'
];
/** {@link PredefinedPeriodIds} allowed on the irrigation dashboard graphic page */
export const allowedIrrigationDashboardGraphPeriods = [
    'current_day',
    'last_7_days',
    'last_14_days',
    'last_30_days'
];
export const defaultIrrigationDashboardGraphPeriod = 'last_7_days';
export const allowedIrrigationDashboardGraphTimeStepByPeriodId = {
    current_day: TimeSteps.perHours,
    last_7_days: TimeSteps.per4Hours,
    last_14_days: TimeSteps.per4Hours,
    last_30_days: TimeSteps.per4Hours
};
export const allowedDssView = [
    'FROST_TRACKING',
    'T_CAPA',
    'HPOT',
    'U_CAPA',
    'T_CAL',
    'LW_V',
    'HEAT_ACCUMULATION',
    'COLD_ACCUMULATION',
    'RR_ACCUMULATION',
    'SSI_HOURS_ACCUMULATION',
    'SSI',
    'T_DEW',
    'NONE',
    'etp',
    'VPD'
];
export const accumulationDssViews = [
    'HEAT_ACCUMULATION',
    'COLD_ACCUMULATION',
    'RR_ACCUMULATION',
    'SSI_HOURS_ACCUMULATION'
];
export const isCumulativeSumDssView = (dssView) => {
    return accumulationDssViews.includes(dssView);
};
/**
 * Excluded metrics because :
 *  - They currently have no translations
 *  - They are displayed elsewhere (e.g in a DSS)
 *  - They're not really readable on a chart
 *  - They're implied by or in some other metric
 */
const metricsToHide = [
    'U_CAPA',
    'T_CAPA',
    'LW_D',
    'LW_V',
    'DXY',
    'T_CAL',
    'FXY',
    'HPOT',
    'DD',
    'T_DEW',
    'SSI',
    'SSI_HOURS',
    'etp',
    'SSI_MAX',
    'SSI_MIN',
    'PPFD_MIN',
    'PPFD_MAX'
];
export const isDisplayableMetric = (metric) => {
    return !metricsToHide.includes(metric);
};
export const defaultMetricToDisplay = [
    'T',
    'RR',
    'U',
    'FF',
    'T_WET',
    'T_DRY',
    'T_SOIL',
    'PPFD'
];
export const defaultDssViewToDisplay = [
    'FROST_TRACKING',
    'LW_V',
    'T_CAPA',
    'T_CAL',
    'HPOT',
    'U_CAPA',
    'SSI'
];
/**
 * Gets the last periods for a given unit of time
 */
const getLastPeriods = (n, unitOfTime, relativeTo, timezone) => ({
    since: moment(relativeTo).tz(timezone).subtract(n, unitOfTime).startOf(unitOfTime).unix(),
    before: moment(relativeTo).tz(timezone).startOf(unitOfTime).unix()
});
/**
 * Gets the last full period of time relative to the given moment, and time unit
 */
export const getLastFullPeriod = (unitOfTime, relativeTo, timezone) => getLastPeriods(1, unitOfTime, relativeTo, timezone);
/**
 * Finds the period relative to an id, a moment & and a timezone
 */
export const getPeriodByPeriodId = ({ periodId, relativeTo: relativeToFromParams, timezone, periodDefined }) => {
    const relativeTo = relativeToFromParams === undefined ? moment().tz(timezone) : relativeToFromParams;
    switch (periodId) {
        case 'current_day':
            return {
                since: moment(relativeTo).tz(timezone).startOf('day').unix(),
                before: moment(relativeTo).tz(timezone).startOf('day').add(1, 'day').unix()
            };
        case 'current_week':
            return {
                since: moment(relativeTo).tz(timezone).startOf('week').unix(),
                before: moment(relativeTo).tz(timezone).endOf('week').add(1, 'day').startOf('day').unix()
            };
        case 'current_month':
            return {
                since: moment(relativeTo).tz(timezone).startOf('month').unix(),
                before: moment(relativeTo).tz(timezone).endOf('month').add(1, 'day').startOf('day').unix()
            };
        case 'current_year':
            return {
                since: moment(relativeTo).tz(timezone).startOf('year').unix(),
                before: moment(relativeTo).tz(timezone).endOf('year').add(1, 'day').startOf('day').unix()
            };
        case 'last_7_days':
            return {
                since: moment(relativeTo).tz(timezone).subtract(1, 'week').startOf('day').unix(),
                before: moment(relativeTo).tz(timezone).add(1, 'day').startOf('day').unix()
            };
        case 'last_14_days':
            return {
                since: moment(relativeTo).tz(timezone).subtract(2, 'week').startOf('day').unix(),
                before: moment(relativeTo).tz(timezone).add(1, 'day').startOf('day').unix()
            };
        case 'last_30_days':
            return {
                since: moment(relativeTo).tz(timezone).subtract(1, 'month').startOf('day').unix(),
                before: moment(relativeTo).tz(timezone).add(1, 'day').startOf('day').unix()
            };
        default:
            return periodDefined
                ? periodDefined
                : getLastFullPeriod('day', moment(relativeTo).add(1, 'days'), timezone);
    }
};
/**
 * Returns the given @param period duration, in the given @param as unit
 */
export const getPeriodDuration = ({ period, as, timezone }) => moment
    .duration(timestampToMoment(period.before, timezone).diff(timestampToMoment(period.since, timezone)))
    .as(as);
/**
 * Adds a metric to the given array
 * Makes sure the array has never more than 2 element in it
 */
export const addMetric = (currentlySelectedMetrics, newMetric) => {
    let finalArray = currentlySelectedMetrics;
    if (!finalArray.includes(newMetric)) {
        if (isEmpty(finalArray) || finalArray.length === 1) {
            finalArray.push(newMetric);
        }
        else {
            finalArray = [currentlySelectedMetrics[1], newMetric];
        }
    }
    return finalArray;
};
/**
 * Removes a metric from the given array
 */
export const removeMetric = (currentlySelectedMetrics, metricToRemove) => {
    const index = currentlySelectedMetrics.indexOf(metricToRemove);
    const finalArray = currentlySelectedMetrics;
    if (index > -1) {
        finalArray.splice(index, 1);
    }
    return finalArray;
};
/**
 * This will transform the @param period since at 00:00 before at 00:00 to since at 00:00 before at 23:59
 */
export const getDisplayablePeriod = (period, timezone) => ({
    since: period.since,
    before: timestampToMoment(period.before, timezone).subtract(1, 'second').unix()
});
function throwBadTimeStep(p) {
    throw new Error('Unknown timestep kind: ' + p);
}
/**
 * Might be useless down the road
 */
const timeStepToUnitOfTime = (timeStep) => {
    switch (timeStep) {
        case TimeSteps.perHalfHours:
            return 'minute';
        case TimeSteps.perQuarterHours:
            return 'minute';
        case TimeSteps.perHours:
            return 'hour';
        case TimeSteps.perDays:
            return 'day';
        case TimeSteps.perWeek:
            return 'week';
        case TimeSteps.perMonth:
            return 'month';
        case TimeSteps.perTrimester:
            return 'month';
        case TimeSteps.perYears:
            return 'year';
        case TimeSteps.per12Hours:
            return 'hour';
        case TimeSteps.per2Hours:
            return 'hour';
        case TimeSteps.per3Hours:
            return 'hour';
        case TimeSteps.per4Hours:
            return 'hour';
        case TimeSteps.per6Hours:
            return 'hour';
        case TimeSteps.per8Hours:
            return 'hour';
        case TimeSteps.maximum:
            return 'millisecond';
        case TimeSteps.custom:
            return 'millisecond';
        default:
            // typescript ensure that this switch is complete
            return throwBadTimeStep(timeStep);
    }
};
export const metricsNotAllowingUnderDay = ['etp'];
const metricsNotAllowingMax = [
    'SSI',
    'SSI_HOURS',
    'T_DEW',
    'etp',
    'U_CAPA',
    'HPOT'
];
const irrigationDssView = hydricStressMetrics;
const dssNotAllowingLastSlidingWeek = allowedDssView.filter((dss) => !irrigationDssView.includes(dss));
function filterTimeStepsPerMetrics(availableTimeSteps, metrics, perDevice) {
    let timeSteps = availableTimeSteps;
    if (metrics.some((m) => metricsNotAllowingUnderDay.includes(m))) {
        timeSteps = timeSteps.filter((timeStep) => !underDayTimeSteps.includes(timeStep));
    }
    else if (!perDevice && metrics.some((m) => metricsNotAllowingMax.includes(m))) {
        timeSteps = timeSteps.filter((timeStep) => timeStep !== TimeSteps.maximum);
    }
    // removing 4 hours time steps if there is no hydric stress metrics
    if (metrics.every((m) => !irrigationDssView.includes(m))) {
        timeSteps = timeSteps.filter((timeStep) => timeStep !== TimeSteps.per4Hours);
    }
    return timeSteps;
}
const MAX_NUMBER_OF_DAYS_IN_A_MONTH = 31;
const MAX_NUMBER_OF_DAYS_IN_TWO_MONTHS = 61;
const MAX_NUMBER_OF_DAYS_IN_A_YEAR = 366;
/**
 * Returns the available time steps depending on the duration of the given period
 */
export const getAvailableTimeStepsForCustomPeriods = ({ metrics, period, timezone, perDevice = false }) => {
    const isNative = isReactNative();
    const limitInDaysForMaximumTimeStep = isNative ? 7 : 14;
    const sinceAsMoment = timestampToMoment(period.since, timezone);
    const beforeAsMoment = timestampToMoment(period.before, timezone);
    const numberOfDaysInPeriod = Math.abs(sinceAsMoment.diff(beforeAsMoment, 'days'));
    let availableTimeSteps = [];
    /**
     * Careful for the following tables definition as the first element of the table will be the default time step
     * So avoid the lowest frequencies has first elements of the table
     */
    // Under a day
    if (numberOfDaysInPeriod <= 1) {
        availableTimeSteps = [TimeSteps.maximum, TimeSteps.perHours, TimeSteps.per4Hours];
    } // Two days up to 2 weeks
    else if (numberOfDaysInPeriod >= 2 && numberOfDaysInPeriod <= limitInDaysForMaximumTimeStep) {
        availableTimeSteps = [
            TimeSteps.perDays,
            TimeSteps.perHours,
            TimeSteps.per4Hours,
            TimeSteps.maximum
        ];
    } // More than two weeks & under a month
    else if (numberOfDaysInPeriod > limitInDaysForMaximumTimeStep && numberOfDaysInPeriod <= 32) {
        availableTimeSteps = [
            TimeSteps.perDays,
            TimeSteps.perHours,
            TimeSteps.per4Hours,
            TimeSteps.perWeek
        ];
    } // Between one and two months
    else if (numberOfDaysInPeriod > MAX_NUMBER_OF_DAYS_IN_A_MONTH &&
        numberOfDaysInPeriod <= MAX_NUMBER_OF_DAYS_IN_TWO_MONTHS) {
        availableTimeSteps = [TimeSteps.perDays, TimeSteps.perWeek];
    } // More than two months & under a year
    else if (numberOfDaysInPeriod > MAX_NUMBER_OF_DAYS_IN_TWO_MONTHS &&
        numberOfDaysInPeriod <= MAX_NUMBER_OF_DAYS_IN_A_YEAR) {
        availableTimeSteps = [TimeSteps.perDays, TimeSteps.perWeek, TimeSteps.perMonth];
    } // More than a year
    else {
        availableTimeSteps = [TimeSteps.perWeek, TimeSteps.perMonth];
    }
    return filterTimeStepsPerMetrics(availableTimeSteps, metrics, perDevice);
};
/**
 * @param metrics selected on the form or chart
 * @returns an array of PredefinedPeriodIds that aren't allowed for the given metrics
 */
export function getForbiddenPeriodIds(metrics, dss) {
    const forbiddenPeriods = [];
    if (!isEmpty(metrics) && metrics.some((m) => metricsNotAllowingUnderDay.includes(m))) {
        forbiddenPeriods.push('current_day');
    }
    if (!isNil(dss) && dssNotAllowingLastSlidingWeek.includes(dss)) {
        forbiddenPeriods.push('last_7_days');
    }
    return forbiddenPeriods;
}
/**
 * Returns the default time step depending on the duration of the given period
 */
export const getDefaultTimeStepPerPeriod = ({ period, selectedMetrics, timezone }) => {
    const sinceAsMoment = timestampToMoment(period.since, timezone);
    const beforeAsMoment = timestampToMoment(period.before, timezone);
    const numberOfDaysInPeriod = Math.abs(sinceAsMoment.diff(beforeAsMoment, 'days'));
    let defaultTimeStep;
    if (intersection(metricsNotAllowingUnderDay, selectedMetrics).length > 0 &&
        numberOfDaysInPeriod <= 14) {
        return TimeSteps.perDays;
    }
    if (numberOfDaysInPeriod < 7) {
        defaultTimeStep = TimeSteps.perHours;
    } // 7 days up to a 14 days -> default per days or per 4h if irrigation
    else if (numberOfDaysInPeriod >= 7 && numberOfDaysInPeriod <= 14) {
        if (intersection(selectedMetrics, [...hydricStressMetrics, ...fertigationMetrics]).length > 0) {
            defaultTimeStep = TimeSteps.per4Hours;
        }
        else {
            defaultTimeStep = TimeSteps.perDays;
        }
    } // Between 14 days and two and half months -> default per days
    else if (numberOfDaysInPeriod > 14 && numberOfDaysInPeriod <= 80) {
        defaultTimeStep = TimeSteps.perDays;
    } // More than two and a half months -> default on the week
    else if (numberOfDaysInPeriod > 80 && numberOfDaysInPeriod < MAX_NUMBER_OF_DAYS_IN_A_YEAR) {
        defaultTimeStep = TimeSteps.perWeek;
    }
    else {
        defaultTimeStep = TimeSteps.perMonth;
    }
    return defaultTimeStep;
};
function checkPeriodId(p) {
    throw new Error('Period not implemented' + p);
}
const movePeriod = ({ period, periodId, timeStep, timezone, isBackward }) => {
    let result;
    const modifier = isBackward ? -1 : 1;
    const sinceAsMoment = timestampToMoment(period.since, timezone);
    const beforeAsMoment = timestampToMoment(period.before, timezone);
    const unit = timeStepToUnitOfTime(timeStep);
    switch (periodId) {
        case 'current_day': {
            const since = sinceAsMoment
                .clone()
                .add(modifier * 1, 'd')
                .startOf('d');
            result = {
                ...period,
                since: since.unix(),
                before: since.clone().add(1, 'd').startOf('d').unix()
            };
            break;
        }
        case 'current_week': {
            const since = sinceAsMoment
                .clone()
                .add(modifier * 1, 'week')
                .startOf('week');
            result = {
                ...period,
                since: since.unix(),
                before: since.clone().add(1, 'week').startOf('d').unix()
            };
            break;
        }
        case 'current_month': {
            const since = sinceAsMoment
                .clone()
                .add(modifier * 1, 'month')
                .startOf('month');
            result = {
                ...period,
                since: since.unix(),
                before: since.clone().add(1, 'month').startOf('d').unix()
            };
            break;
        }
        case 'current_year': {
            const since = sinceAsMoment
                .clone()
                .add(modifier * 1, 'year')
                .startOf('year');
            result = {
                ...period,
                since: since.unix(),
                before: since.clone().add(1, 'year').startOf('d').unix()
            };
            break;
        }
        case 'last_7_days': {
            const since = sinceAsMoment
                .clone()
                .add(modifier * 8, 'days')
                .startOf('day');
            result = {
                ...period,
                since: since.unix(),
                before: since.clone().add(8, 'days').startOf('d').unix()
            };
            break;
        }
        case 'last_14_days': {
            const since = sinceAsMoment
                .clone()
                .add(modifier * 15, 'days')
                .startOf('day');
            result = {
                ...period,
                since: since.unix(),
                before: since.clone().add(15, 'days').startOf('d').unix()
            };
            break;
        }
        case 'last_30_days': {
            const since = sinceAsMoment
                .clone()
                .add(modifier * 30, 'days')
                .startOf('day');
            result = {
                ...period,
                since: since.unix(),
                before: since.clone().add(30, 'days').startOf('d').unix()
            };
            break;
        }
        case 'custom':
            {
                const duration = moment
                    .duration(beforeAsMoment.clone().tz(timezone).diff(sinceAsMoment.clone().tz(timezone), unit), unit)
                    .asMilliseconds();
                result = {
                    ...period,
                    since: sinceAsMoment
                        .add(modifier * duration, 'milliseconds')
                        .startOf(unit)
                        .unix(),
                    before: beforeAsMoment
                        .add(modifier * duration, 'milliseconds')
                        .startOf(unit)
                        .unix()
                };
            }
            break;
        default:
            // force switch completeness with typescript
            checkPeriodId(periodId);
    }
    return result;
};
/**
 * Given a period this will return the previous period (same duration in the past)
 */
export const getPreviousPeriodOf = ({ period, periodId, timeStep, timezone }) => {
    return movePeriod({
        period,
        periodId,
        timeStep,
        timezone,
        isBackward: true
    });
};
/**
 * Given a period this will return the next period (same duration in the future)
 */
export const getNextPeriodOf = ({ period, periodId, timeStep, timezone }) => {
    return movePeriod({
        period,
        periodId,
        timeStep,
        timezone,
        isBackward: false
    });
};
/**
 * Returns an array of metric ids corresponding to the aggregates of the given metric
 */
export const getMetricAggregates = (metric) => {
    const aggregateMetrics = [];
    // temporary workaround until backends uses X-min and X-max for U & T
    if (isAggregateEnabledMetric(metric)) {
        switch (metric) {
            case 'T':
                aggregateMetrics.push('TN', 'TX');
                break;
            case 'U':
                aggregateMetrics.push('UN', 'UX');
                break;
            case 'FF':
                aggregateMetrics.push('FXY');
                break;
            default:
                aggregateMetrics.push(`${metric}-min`, `${metric}-max`);
                break;
        }
    }
    return aggregateMetrics;
};
function throwOnUnknownDssView(p) {
    throw new Error('Unknown DSSView : ' + p);
}
export const getDssMetrics = (dssView) => {
    switch (dssView) {
        /**
         * Can't implement ACCUMULATION views here otherwise it will show on the GraphCreationForm
         * see {@link getAccumulationMetricFromCumulativeSumDssView} instead
         */
        case 'NONE':
        case 'HEAT_ACCUMULATION':
        case 'COLD_ACCUMULATION':
        case 'RR_ACCUMULATION':
        case 'SSI_HOURS_ACCUMULATION':
            return { metrics: [], measurementReports: [] };
        case 'FROST_TRACKING':
            return { metrics: ['T_WET', 'T_DRY'], measurementReports: [] };
        case 'T_DEW':
            return { metrics: ['T', 'T_DEW'], measurementReports: [] };
        case 'HPOT':
            return {
                metrics: ['HPOT', 'RR', 'IRR'],
                measurementReports: ['rainfalls'],
                waterIncome: ['irrigation', 'rainfalls']
            };
        case 'U_CAPA':
            return {
                metrics: ['U_CAPA', 'RR', 'IRR'],
                measurementReports: ['rainfalls'],
                waterIncome: ['irrigation', 'rainfalls']
            };
        case 'T_CAPA':
        case 'T_CAL':
        case 'LW_V':
        case 'SSI':
        case 'etp':
        case 'VPD':
            return { metrics: [dssView], measurementReports: [] };
        default:
            // typescript ensure that this switch is complete
            return throwOnUnknownDssView(dssView);
    }
};
const historyCompatibleCumulativeSumMetrics = [
    'heatAccumulation',
    'COLD_T_HOUR',
    'RRAccumulation',
    'SSI_HOURSAccumulation'
];
export const isHistoryCompatibleCumulativeSumMetric = (metric) => {
    return historyCompatibleCumulativeSumMetrics.includes(metric);
};
export const getCumulativeSumDssViewFromMetric = (metric) => {
    switch (metric) {
        case 'COLD_T_HOUR':
            return 'COLD_ACCUMULATION';
        case 'heatAccumulation':
            return 'HEAT_ACCUMULATION';
        case 'RRAccumulation':
            return 'RR_ACCUMULATION';
        case 'SSI_HOURSAccumulation':
            return 'SSI_HOURS_ACCUMULATION';
        default:
            throw new Error(`getCumulativeSumDssViewFromMetric is not implemented for ${metric}`);
    }
};
export const getAccumulationMetricFromCumulativeSumDssView = (dssView) => {
    switch (dssView) {
        case 'COLD_ACCUMULATION':
            return 'COLD_T_HOUR';
        case 'HEAT_ACCUMULATION':
            return 'heatAccumulation';
        case 'RR_ACCUMULATION':
            return 'RRAccumulation';
        case 'SSI_HOURS_ACCUMULATION':
            return 'SSI_HOURSAccumulation';
        default:
            throw new Error(`getAccumulationMetricFromCumulativeSumDssView is not implemented for ${dssView}`);
    }
};
/**
 * Returns an array of FormattedDataPoint to trace a min-max area
 */
const getAggregateDataFromHistoryData = (historyData, metric, conversionFct) => {
    if (isMetricWithHorizon(metric)) {
        throw new Error('aggregate and horizon not implemented yet');
    }
    const result = { min: [], max: [] };
    if (!isEmpty(historyData)) {
        const formattedMax = formatHistoryDataForCharts({
            data: historyData,
            metric: metric,
            aggregateToExtract: AggregationType.max,
            conversionFct
        });
        const formattedMin = formatHistoryDataForCharts({
            data: historyData,
            metric: metric,
            aggregateToExtract: AggregationType.min,
            conversionFct
        });
        result.max = formattedMax;
        result.min = formattedMin;
    }
    return result;
};
/**
 * Returns an array of FormattedDataPoint to trace a min-max area of a specific metric
 */
export const getAggregateDataForMetric = (historyData, metric, conversionFct) => {
    let result = { min: [], max: [] };
    // temporary workaround until backends uses X-min and X-max for U & T
    switch (metric) {
        case 'T':
            if (!isEmpty(historyData)) {
                const formattedMax = formatHistoryDataForCharts({
                    data: historyData,
                    metric: 'TX',
                    aggregateToExtract: AggregationType.max,
                    conversionFct
                });
                const formattedMin = formatHistoryDataForCharts({
                    data: historyData,
                    metric: 'TN',
                    aggregateToExtract: AggregationType.min,
                    conversionFct
                });
                result.max = formattedMax;
                result.min = formattedMin;
            }
            break;
        case 'U':
            if (!isEmpty(historyData)) {
                const formattedMax = formatHistoryDataForCharts({
                    data: historyData,
                    metric: 'UX',
                    aggregateToExtract: AggregationType.max,
                    conversionFct
                });
                const formattedMin = formatHistoryDataForCharts({
                    data: historyData,
                    metric: 'UN',
                    aggregateToExtract: AggregationType.min,
                    conversionFct
                });
                result.max = formattedMax;
                result.min = formattedMin;
            }
            break;
        // specific case for winds, we use FXY as maxes but have to specify a min as 0 in order to display the area
        case 'FF':
            if (!isEmpty(historyData)) {
                const formattedMax = formatHistoryDataForCharts({
                    data: historyData,
                    metric: 'FXY',
                    aggregateToExtract: AggregationType.max,
                    conversionFct
                });
                const formattedMin = formattedMax.map((pt) => ({ ...pt, y: 0, value: 0 }));
                result.max = formattedMax;
                result.min = formattedMin;
            }
            break;
        default:
            result = getAggregateDataFromHistoryData(historyData, metric, conversionFct);
            break;
    }
    return result;
};
/**
 * Returns an array of metric ids to fetch
 * Used for metrics that needs other metrics to be displayed properly
 * e.g : wind also needs wind direction and wind gusts
 */
export const getMetricsToFetch = (selectedMetrics) => {
    let metricsToFetch = selectedMetrics.reduce((acc, metric) => [...acc, metric, ...getMetricAggregates(metric)], []);
    // Adding wind gusts & wind direction when wind is selected
    if (metricsToFetch.includes('FF')) {
        metricsToFetch = [...metricsToFetch, 'DD'];
    }
    return metricsToFetch;
};
/** Transforms a period */
export const getPeriodAsSentence = (period, timezone) => {
    const displayablePeriod = getDisplayablePeriod(period, timezone);
    const { since: displayableSince, before: displayableBefore } = displayablePeriod;
    const sinceInTimezone = timestampToMoment(displayableSince, timezone);
    const beforeInTimezone = timestampToMoment(displayableBefore, timezone);
    const isSameYear = sinceInTimezone.isSame(beforeInTimezone, 'year');
    const isSameMonthAndYear = sinceInTimezone.isSame(beforeInTimezone, 'month') && isSameYear;
    const formattedDate = sinceInTimezone.format(weenatIntl.t('formats.day'));
    let formattedSince = sinceInTimezone.format(weenatIntl.t('formats.date'));
    let formattedBefore = beforeInTimezone.format(weenatIntl.t('formats.date'));
    const periodDuration = getPeriodDuration({
        as: 'days',
        period,
        timezone
    });
    const isPeriodShorterThanADay = periodDuration <= 1;
    if (isPeriodShorterThanADay) {
        formattedSince = sinceInTimezone.format(weenatIntl.t('formats.time'));
        formattedBefore = beforeInTimezone.format(weenatIntl.t('formats.time'));
    }
    else {
        formattedSince = sinceInTimezone.format(weenatIntl.t('formats.date'));
        formattedBefore = beforeInTimezone.format(weenatIntl.t('formats.date'));
        if (isSameYear) {
            formattedSince = sinceInTimezone.format('DD MMM');
        }
        if (isSameMonthAndYear) {
            formattedSince = sinceInTimezone.format('DD');
        }
    }
    const formatTitle = (msgKey, capitalizeMsg = true) => weenatIntl.t(msgKey, {
        date: formattedDate,
        since: formattedSince,
        before: formattedBefore
    }, { capitalize: capitalizeMsg });
    if (isPeriodShorterThanADay) {
        return formatTitle('periods.custom_with_values_in_hours');
    }
    else {
        return formatTitle('periods.custom_with_values');
    }
};
/**
 * Returns all the available DSS View of a plot given it's measures and restrictions
 * @param availableMeasures
 * @param restrictedMeasures
 * @returns a list of {@link AllowedDssView} with their restricted state
 */
const getAvailableDssViews = (availableMeasures, restrictedMeasures) => {
    const availableDss = [];
    if (availableMeasures.includes('T_CAL') && allowedDssView.includes('T_CAL')) {
        availableDss.push({ value: 'T_CAL', isRestricted: restrictedMeasures.includes('T_CAL') });
    }
    if (availableMeasures.includes('T_CAPA') && allowedDssView.includes('T_CAPA')) {
        availableDss.push({ value: 'T_CAPA', isRestricted: restrictedMeasures.includes('T_CAPA') });
    }
    if (availableMeasures.includes('T_DRY') && allowedDssView.includes('FROST_TRACKING')) {
        availableDss.push({
            value: 'FROST_TRACKING',
            isRestricted: restrictedMeasures.includes('T_DRY')
        });
    }
    if (availableMeasures.includes('LW_V') && allowedDssView.includes('LW_V')) {
        availableDss.push({
            value: 'LW_V',
            isRestricted: restrictedMeasures.includes('LW_V')
        });
    }
    if (availableMeasures.includes('T') && allowedDssView.includes('HEAT_ACCUMULATION')) {
        availableDss.push({
            value: 'HEAT_ACCUMULATION',
            isRestricted: restrictedMeasures.includes('T')
        });
    }
    if (availableMeasures.includes('T') && allowedDssView.includes('COLD_ACCUMULATION')) {
        availableDss.push({
            value: 'COLD_ACCUMULATION',
            isRestricted: restrictedMeasures.includes('T')
        });
    }
    if (availableMeasures.includes('RR') && allowedDssView.includes('RR_ACCUMULATION')) {
        availableDss.push({
            value: 'RR_ACCUMULATION',
            isRestricted: restrictedMeasures.includes('RR')
        });
    }
    if (availableMeasures.includes('SSI_HOURS') &&
        allowedDssView.includes('SSI_HOURS_ACCUMULATION')) {
        availableDss.push({
            value: 'SSI_HOURS_ACCUMULATION',
            isRestricted: restrictedMeasures.includes('SSI_HOURS')
        });
    }
    if (availableMeasures.includes('SSI') && allowedDssView.includes('SSI')) {
        availableDss.push({
            value: 'SSI',
            isRestricted: restrictedMeasures.includes('SSI')
        });
    }
    if (availableMeasures.includes('T_DEW') && allowedDssView.includes('T_DEW')) {
        availableDss.push({
            value: 'T_DEW',
            isRestricted: restrictedMeasures.includes('T_DEW')
        });
    }
    if (availableMeasures.includes('etp') && allowedDssView.includes('etp')) {
        availableDss.push({
            value: 'etp',
            isRestricted: restrictedMeasures.includes('etp')
        });
    }
    if (availableMeasures.includes('U_CAPA') && allowedDssView.includes('U_CAPA')) {
        availableDss.push({
            value: 'U_CAPA',
            isRestricted: restrictedMeasures.includes('U_CAPA')
        });
    }
    if (availableMeasures.includes('HPOT') && allowedDssView.includes('HPOT')) {
        availableDss.push({
            value: 'HPOT',
            isRestricted: restrictedMeasures.includes('HPOT')
        });
    }
    if (availableMeasures.includes('T') &&
        availableMeasures.includes('U') &&
        allowedDssView.includes('VPD')) {
        availableDss.push({
            value: 'VPD',
            isRestricted: restrictedMeasures.includes('T') || restrictedMeasures.includes('U')
        });
    }
    return availableDss;
};
export function getDssViewLabel(dssView) {
    const capitalize = true;
    switch (dssView) {
        case 'T_CAL':
            return weenatIntl.t('metrics.T_CAL', { capitalize });
        case 'T_CAPA':
            return weenatIntl.t('metrics.T_CAPA', { capitalize });
        case 'FROST_TRACKING':
            return weenatIntl.t('dss.frostTracking.dss.name', { capitalize });
        case 'LW_V':
            return weenatIntl.t('metrics.LW_V', { capitalize });
        case 'COLD_ACCUMULATION':
            return weenatIntl.t('metrics.COLD_T_HOUR', { capitalize });
        case 'HEAT_ACCUMULATION':
            return weenatIntl.t('metrics.heatAccumulation', { capitalize });
        case 'RR_ACCUMULATION':
            return weenatIntl.t('metrics.RRAccumulation', { capitalize });
        case 'SSI_HOURS_ACCUMULATION':
            return weenatIntl.t('metrics.SSI_HOURSAccumulation', { capitalize });
        case 'SSI':
            return weenatIntl.t('dss.solarRadiation.dss.name', { capitalize });
        case 'T_DEW':
            return weenatIntl.t('dss.dewPoint.dss.name', { capitalize });
        case 'etp':
            return weenatIntl.t('metrics.etp', { capitalize });
        case 'U_CAPA':
            return weenatIntl.t('dss.capacitiveIrrigation.dss.name', { capitalize });
        case 'HPOT':
            return weenatIntl.t('dss.irritensio.dss.name', { capitalize });
        case 'VPD':
            return weenatIntl.t('metrics.VPD', { capitalize });
        case 'NONE':
            return '-';
        default:
            return throwOnUnknownDssView(dssView);
    }
}
export const getAvailableDssViewsAsOptions = (availableMeasures, restrictedMeasures) => {
    const availableDss = getAvailableDssViews(availableMeasures, restrictedMeasures);
    const options = availableDss.map((dss) => {
        return {
            ...dss,
            label: getDssViewLabel(dss.value)
        };
    });
    return options;
};
/**
 * get period without forecast from period and time step
 */
export const getPeriodWithoutForecast = ({ period, timeStep, timezone }) => {
    let result;
    const sinceMoment = timestampToMoment(period.since, timezone);
    const beforeMoment = timestampToMoment(period.before, timezone);
    const now = moment(new Date(Date.now())).tz(timezone);
    const isSinceAfterNow = sinceMoment.isAfter(now);
    const isBeforeAfterNow = beforeMoment.isAfter(now);
    if (isSinceAfterNow) {
        result = undefined;
    }
    else if (!isBeforeAfterNow) {
        result = period;
    }
    else {
        switch (timeStep) {
            // following cases 2,3,6,8 & 12 are arbitrary and may need to be removed along with additionalAggregates
            case TimeSteps.perQuarterHours:
            case TimeSteps.perHalfHours:
            case TimeSteps.perHours:
            case TimeSteps.per4Hours:
            case TimeSteps.per2Hours:
            case TimeSteps.per3Hours:
            case TimeSteps.per6Hours:
            case TimeSteps.per8Hours:
            case TimeSteps.per12Hours: {
                const nextHour = moment(new Date(Date.now())).tz(timezone).startOf('hour');
                result = { ...period, before: nextHour.unix() };
                break;
            }
            case TimeSteps.custom:
            case TimeSteps.perDays: {
                const tomorrow = moment(new Date(Date.now())).tz(timezone).add(1, 'day').startOf('day');
                result = { ...period, before: tomorrow.unix() };
                break;
            }
            case TimeSteps.maximum:
                result = period;
                break;
            case TimeSteps.perMonth:
            case TimeSteps.perTrimester: {
                const nextMonth = moment(new Date(Date.now())).tz(timezone).add(1, 'month').startOf('month');
                result = { ...period, before: nextMonth.unix() };
                break;
            }
            case TimeSteps.perWeek: {
                const nextWeek = moment(new Date(Date.now())).tz(timezone).add(1, 'week').startOf('week');
                result = { ...period, before: nextWeek.unix() };
                break;
            }
            case TimeSteps.perYears: {
                const nextYear = moment(new Date(Date.now())).tz(timezone).add(1, 'year').startOf('year');
                result = { ...period, before: nextYear.unix() };
                break;
            }
            default:
                throwBadTimeStep(timeStep);
        }
    }
    return result;
};
export const dateTupleToHistoryPeriod = (range, timezone) => {
    const since = moment(range[0]).tz(timezone).unix();
    const before = moment(range[1]).tz(timezone).unix();
    return { since, before };
};
export const historyPeriodToDateTuple = (period, timezone) => {
    const start = timestampToMoment(period.since, timezone).toDate();
    const end = timestampToMoment(period.before, timezone).toDate();
    return [start, end];
};
export const fallbackOnAvailableOrDefaultSelections = ({ restrictedMetrics, availableMeasures, selectedMetrics, selectedDssView, dispatch }) => {
    const availableDssViews = getAvailableDssViews(availableMeasures, restrictedMetrics).map(({ value }) => value);
    const selectedMetricsIntersection = intersection(availableMeasures, selectedMetrics);
    const selectedDssIntersection = intersection(availableDssViews, [selectedDssView]);
    const matchOnSelectedMetrics = !isEmpty(selectedMetricsIntersection);
    const matchOnSelectedDss = !isEmpty(selectedDssIntersection);
    const nothingSelected = selectedMetrics.length === 0 && selectedDssView === 'NONE';
    const noMatchBetweenAvailableAndSelections = isEmpty(selectedMetricsIntersection) && isEmpty(selectedDssIntersection);
    if (nothingSelected || noMatchBetweenAvailableAndSelections) {
        const defaultMetricsIntersection = intersection(defaultMetricToDisplay, availableMeasures);
        const defaultDssIntersection = intersection(defaultDssViewToDisplay, availableDssViews);
        const matchOnDefaultMetrics = !isEmpty(defaultMetricsIntersection);
        const matchOnDefaultDssView = !isEmpty(defaultDssIntersection);
        if (matchOnDefaultMetrics) {
            dispatch({
                type: 'setMetrics',
                newMetrics: defaultMetricsIntersection.slice(0, 2)
            });
        }
        else if (matchOnDefaultDssView) {
            dispatch({
                type: 'setDssView',
                newDssView: defaultDssIntersection[0]
            });
        }
        // final safety to make sure we fallback on something if a metric isn't part of defaultMetricToDisplay or defaultDssViewToDisplay
        else {
            dispatch({
                type: 'setMetrics',
                newMetrics: availableMeasures.slice(0, 1)
            });
        }
    }
    else {
        if (matchOnSelectedDss) {
            dispatch({
                type: 'setDssView',
                newDssView: selectedDssIntersection[0]
            });
        }
        else if (matchOnSelectedMetrics) {
            dispatch({
                type: 'setMetrics',
                newMetrics: selectedMetricsIntersection.slice(0, 2)
            });
        }
    }
};
/**
 * Gets the preferred way to aggregate data over a period (sum or average)
 * @returns periodAggregation of the given metric
 */
export const getPeriodAggregationPerMetric = (metric) => {
    let aggregation;
    switch (metric) {
        case 'IRR':
        case 'RR':
        case 'etp':
        case 'heatAccumulation':
        case 'RRAccumulation':
        case 'COLD_T_HOUR':
        case 'SSI_HOURS':
        case 'SSI_HOURSAccumulation':
            aggregation = AggregationType.sum;
            break;
        case 'T':
        case 'T_SOIL':
        case 'T_WET':
        case 'T_DRY':
        case 'T_DEW':
        case 'U':
        case 'FF':
        case 'SSI':
            aggregation = AggregationType.avg;
            break;
        default:
            aggregation = undefined;
            break;
    }
    return aggregation;
};
export const metricsAllowingAggregatesOnMaxTimestep = ['FF'];
/** above this mesure it's a sunny day */
export const SURFACE_POWER_THRESHOLD = 120;
